import React, { useState, useEffect, useCallback } from 'react';
import { employeePayRateAPI, timePunchAPI, leaveConfigAPI } from '../services/api';
import type { EmployeePayRate, EmployeeLeaveConfig } from '../types';

interface UserOption {
  id: number;
  username: string;
  full_name: string;
}

const AdminPayRates: React.FC = () => {
  const [rates, setRates] = useState<EmployeePayRate[]>([]);
  const [currentRates, setCurrentRates] = useState<EmployeePayRate[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [formUser, setFormUser] = useState('');
  const [formRate, setFormRate] = useState('');
  const [formEffectiveDate, setFormEffectiveDate] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [viewMode, setViewMode] = useState<'current' | 'all'>('current');

  const [leaveConfigs, setLeaveConfigs] = useState<EmployeeLeaveConfig[]>([]);
  const [editingLeaveConfigId, setEditingLeaveConfigId] = useState<number | null>(null);
  const [editVacHours, setEditVacHours] = useState('');
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveFormUser, setLeaveFormUser] = useState('');
  const [leaveFormHours, setLeaveFormHours] = useState('0');

  const fetchRates = useCallback(async () => {
    try {
      const [allResp, currentResp] = await Promise.all([
        employeePayRateAPI.getAll(),
        employeePayRateAPI.getCurrentRates(),
      ]);
      setRates(allResp.data.results);
      setCurrentRates(currentResp.data);
    } catch (err) {
      console.error('Failed to fetch pay rates:', err);
    }
  }, []);

  const fetchLeaveConfigs = useCallback(async () => {
    try {
      const resp = await leaveConfigAPI.getAll();
      setLeaveConfigs(resp.data.results || []);
    } catch {
      setLeaveConfigs([]);
    }
  }, []);

  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      try {
        const usersResp = await timePunchAPI.getEmployees();
        setUsers(usersResp.data);
      } catch (err) {
        console.error('Failed to load users:', err);
      }
      await Promise.all([fetchRates(), fetchLeaveConfigs()]);
      setLoading(false);
    };
    loadInitial();
  }, [fetchRates, fetchLeaveConfigs]);

  const resetForm = () => {
    setShowForm(false);
    setFormUser('');
    setFormRate('');
    setFormEffectiveDate('');
    setFormNotes('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formUser || !formRate || !formEffectiveDate) {
      setError('Employee, rate, and effective date are required.');
      return;
    }

    try {
      await employeePayRateAPI.create({
        user: Number(formUser),
        hourly_rate: formRate,
        effective_date: formEffectiveDate,
        notes: formNotes,
      });
      setSuccess('Pay rate added successfully.');
      resetForm();
      await fetchRates();
    } catch (err: any) {
      const detail = err.response?.data;
      const msg = typeof detail === 'string' ? detail : (detail?.detail || detail?.error || JSON.stringify(detail));
      setError(msg || 'Failed to save pay rate.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this pay rate record?')) return;
    try {
      await employeePayRateAPI.delete(id);
      setSuccess('Pay rate deleted.');
      await fetchRates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete.');
    }
  };

  if (loading) {
    return <div className="container"><div className="loading">Loading...</div></div>;
  }

  const displayRates = viewMode === 'current' ? currentRates : rates;

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ marginBottom: 0 }}>Employee Pay Rates</h2>
          <button className="btn btn-success" onClick={() => { resetForm(); setShowForm(true); }}>
            + Add Rate
          </button>
        </div>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        {showForm && (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #ddd',
          }}>
            <h3 style={{ marginBottom: '1rem' }}>New Pay Rate</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Employee</label>
                  <select value={formUser} onChange={(e) => setFormUser(e.target.value)} required>
                    <option value="">Select...</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Hourly Rate ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formRate}
                    onChange={(e) => setFormRate(e.target.value)}
                    placeholder="e.g. 25.00"
                    required
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Effective Date</label>
                  <input
                    type="date"
                    value={formEffectiveDate}
                    onChange={(e) => setFormEffectiveDate(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '1rem', marginBottom: 0 }}>
                <label>Notes (optional)</label>
                <input
                  type="text"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="e.g. Annual raise, starting rate..."
                />
              </div>
              <div className="button-group">
                <button type="submit" className="btn btn-primary">Save</button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <button
            className={`btn btn-small ${viewMode === 'current' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('current')}
            style={{ marginRight: '0.5rem' }}
          >
            Current Rates
          </button>
          <button
            className={`btn btn-small ${viewMode === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('all')}
          >
            Rate History
          </button>
        </div>

        {displayRates.length === 0 ? (
          <p style={{ color: '#95a5a6' }}>No pay rates found. Add a rate to get started.</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Hourly Rate</th>
                  <th>Effective Date</th>
                  <th>Notes</th>
                  {viewMode === 'all' && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {displayRates.map(rate => (
                  <tr key={rate.id}>
                    <td>{rate.user_full_name || rate.user_username}</td>
                    <td style={{ fontWeight: 'bold', color: '#27ae60' }}>${rate.hourly_rate}</td>
                    <td>{new Date(rate.effective_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td style={{ color: '#7f8c8d' }}>{rate.notes || '—'}</td>
                    {viewMode === 'all' && (
                      <td className="actions">
                        <button className="btn btn-small btn-danger" onClick={() => handleDelete(rate.id!)}>Delete</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Leave Entitlements Section */}
      <div className="card" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ marginBottom: 0 }}>Vacation Entitlements</h2>
          <button className="btn btn-success" onClick={() => { setShowLeaveForm(true); setLeaveFormUser(''); setLeaveFormHours('0'); }}>
            + Add Entitlement
          </button>
        </div>

        {showLeaveForm && (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #ddd',
          }}>
            <h3 style={{ marginBottom: '1rem' }}>New Vacation Entitlement</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setError(''); setSuccess('');
              if (!leaveFormUser) { setError('Select an employee.'); return; }
              try {
                await leaveConfigAPI.create({
                  user: Number(leaveFormUser),
                  vacation_annual_hours: leaveFormHours,
                });
                setSuccess('Vacation entitlement created.');
                setShowLeaveForm(false);
                await fetchLeaveConfigs();
              } catch (err: any) {
                const detail = err.response?.data;
                const msg = typeof detail === 'string' ? detail : (detail?.user?.[0] || detail?.detail || JSON.stringify(detail));
                setError(msg || 'Failed to save.');
              }
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Employee</label>
                  <select value={leaveFormUser} onChange={(e) => setLeaveFormUser(e.target.value)} required>
                    <option value="">Select...</option>
                    {users.filter(u => !leaveConfigs.some(c => c.user === u.id)).map(u => (
                      <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Vacation Hours / Year</label>
                  <input
                    type="number" step="0.5" min="0"
                    value={leaveFormHours} onChange={(e) => setLeaveFormHours(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="button-group">
                <button type="submit" className="btn btn-primary">Save</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowLeaveForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {leaveConfigs.length === 0 ? (
          <p style={{ color: '#95a5a6' }}>No vacation entitlements configured. Add one to get started.</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Vacation Hours / Year</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leaveConfigs.map(config => (
                  <tr key={config.id}>
                    <td>{config.user_full_name || config.user_username}</td>
                    <td>
                      {editingLeaveConfigId === config.id ? (
                        <input
                          type="number" step="0.5" min="0"
                          value={editVacHours}
                          onChange={(e) => setEditVacHours(e.target.value)}
                          style={{ width: '100px' }}
                          autoFocus
                        />
                      ) : (
                        <span style={{ fontWeight: 'bold', color: Number(config.vacation_annual_hours) > 0 ? '#3498db' : '#95a5a6' }}>
                          {config.vacation_annual_hours}h
                          {Number(config.vacation_annual_hours) === 0 && <span style={{ marginLeft: '0.5rem', fontWeight: 'normal' }}>(none)</span>}
                        </span>
                      )}
                    </td>
                    <td className="actions">
                      {editingLeaveConfigId === config.id ? (
                        <>
                          <button className="btn btn-small btn-primary" onClick={async () => {
                            try {
                              await leaveConfigAPI.update(config.id!, { vacation_annual_hours: editVacHours });
                              setEditingLeaveConfigId(null);
                              setSuccess('Updated.');
                              await fetchLeaveConfigs();
                            } catch (err: any) {
                              setError('Failed to update.');
                            }
                          }}>Save</button>
                          <button className="btn btn-small btn-secondary" onClick={() => setEditingLeaveConfigId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-small btn-primary" onClick={() => {
                            setEditingLeaveConfigId(config.id!);
                            setEditVacHours(config.vacation_annual_hours);
                          }}>Edit</button>
                          <button className="btn btn-small btn-danger" onClick={async () => {
                            if (!window.confirm('Delete this vacation entitlement?')) return;
                            try {
                              await leaveConfigAPI.delete(config.id!);
                              setSuccess('Deleted.');
                              await fetchLeaveConfigs();
                            } catch {
                              setError('Failed to delete.');
                            }
                          }}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPayRates;
